import React, { Suspense, useContext, useEffect } from "react"
import OrderDetailHOC from "@Hoc/OrderDetailHOC"
import PackageAction from "@Components/OrderDetail/PackageProductOrder/PackageAction"
import SummaryProductList from "@Components/OrderDetail/SummaryProducts/SummaryProductsList"
import { OrderDetailType } from "@Types/OrderType"
import usePackageOrder from "@Pages/DetailOrderScreens/Package/UsePackageOrder"
import { IconSpinner } from "@Theme/common/Icon.styled"
import SkeletonShippinGroupDetail from "@Components/Commons/Skeleton/SkeletonShippinGroupDetail"
import TagPrintContent from "@Components/OrderDetail/TagPrint/TagPrintContent"
import { AuthContext } from "@Context/context/AuthContext"
import { clients } from "@Webapp-Clients/Clients"
import useHideActionButtons from "@Hooks/UseHideActionButtons"
import { WebAppClientsEnum } from "@Enums/CommonEnum"
import { IconCancel } from "@Theme/common/Icon.styled"
import { ButtonStyled } from "@Theme/common/Button.styled"
import ContentDetailOrderActionsStyled from "@Theme/common/ContentDetailOrderActions.styled"
import UseCancelOrder from "@Hooks/UseCancelOrder"
import { useGetCancelActionByClient } from "@Hooks/UseGetMultiClient"

const InfoElocker = React.lazy(() => import("@Components/OrderDetail/ElockerQR/InfoElocker"))

const PackageScreen = ({
  order,
  shippinGroupDetail,
  shippingGroupId,
  finishedShippingGroupAction,
  setFinishedShippingGroupAction,
}: OrderDetailType) => {
  const {
    state: { user },
  } = useContext(AuthContext)
  const {hidePackageActions, updateVisibilityButtonActions} = useHideActionButtons()

  useEffect(() => {
    updateVisibilityButtonActions(
      shippinGroupDetail?.source?.id,
      shippinGroupDetail?.target?.source?.id
    )
  }, [shippinGroupDetail, updateVisibilityButtonActions])

  const client = clients[user?.mainGroup.id as keyof typeof clients]
  const FilesList = client["FilesList" as keyof typeof client]

  const itemsOrder = shippinGroupDetail?.source.items
  const elockerInfo = shippinGroupDetail?.custom?.infoElocker
  const documents = shippinGroupDetail?.custom?.documents

  const {
    loadingPackage,
    handleClickPackageOrder,
    canViewQrCancel,
    canViewQrEntry,
    canViewQrExit,
    denyPackingAction,
  } = usePackageOrder({
    shippingGroupId,
    setFinishedShippingGroupAction,
    isElocker: elockerInfo?.isElocker,
  })

  const [permissionOrder, actionOrder] = useGetCancelActionByClient()

  const handleClickCancel = () => {
    handleClickCancelOrder(actionOrder)
  }

  const {
    handleClickCancelOrder,
    loadingCancel,
    canIuseCancel
  } = UseCancelOrder({
    order,
    shippingGroupId,
    permissionOrder,
  })

  return (
    <>
      {itemsOrder ? (
        <>
        
          {canIuseCancel && window?.$website === WebAppClientsEnum.sandbox && (
            <ContentDetailOrderActionsStyled>
              <ButtonStyled
                variant="secondary"
                onClick={handleClickCancel}
                loadingButton={loadingCancel}
                disabled={loadingCancel}
              >
                <IconCancel />
                <span>Cancelar Orden</span>
              </ButtonStyled>
            </ContentDetailOrderActionsStyled>
          )}

          <SummaryProductList
            itemsOrder={itemsOrder}
            groupPicking={false}
            titleMain={finishedShippingGroupAction ? "Productos Empacados" : undefined}
          />

          {elockerInfo?.isElocker && (
            <Suspense
              fallback={
                <>
                  <IconSpinner variant="big" className="content-center" />
                  <p className="padding-content">Cargando información Elocker</p>
                </>
              }
            >
              <InfoElocker
                codigoCancelacion={canViewQrCancel ? elockerInfo.codigoCancelacion : null}
                codigoEntrega={canViewQrEntry ? elockerInfo.codigoEntrega : null}
                codigoRetiro={canViewQrExit ? elockerInfo.codigoRetiro : null}
                puerta={elockerInfo.puerta}
              />
            </Suspense>
          )}

          {!finishedShippingGroupAction && !denyPackingAction && !hidePackageActions && (
            <PackageAction
              loadingPaking={loadingPackage}
              onClickPackageOrder={handleClickPackageOrder}
            />
          )}
          {order && (
            <TagPrintContent
              codigoEntrega={(elockerInfo?.isElocker && elockerInfo?.codigoEntrega) || null}
              order={order}
              shippingGroupId={shippingGroupId}
            />
          )}
          {FilesList && (
            <Suspense fallback={null}>
              <FilesList
                documents={documents}
                channel={shippinGroupDetail?.channel}
                orderId={order?.id}
              />
            </Suspense>
          )}
        </>
      ) : (
        <SkeletonShippinGroupDetail />
      )}
    </>
  )
}

export default OrderDetailHOC(PackageScreen)
